/* eslint-disable @typescript-eslint/naming-convention */
import React, { useCallback, useContext, useMemo } from "react";
import { useEffectOnce } from "@swiggy-private/core-ui";
// components
import { Button } from "@swiggy-private/dls-web-core/dist/components/Button";
import { Text } from "@swiggy-private/dls-web-core/dist/components/Text";
import { BottomSheet, Image } from "@swiggy-private/core-ui";
// types
import { M2CCardComms, TGCTAs } from "@swiggy-private/cart-types";
import { BullsEyeTGHalfCardProps } from "./types";
// constants
import { BULLS_EYE_TG } from "@swiggy-private/cart-test-ids";
import { PLACEHOLDERS, FULL_RAY_BACKGROUND, DELIVERY_EXECUTIVE, HALF_RAY_BACKGROUND } from "./constants";
// utils
import { getCloudinaryUrl, isEmpty } from "@swiggy-private/js-utils";
// styles
import {
    BullsEyeHalfCard,
    DEAsset,
    HalfCardContainer,
    HalfCardDetails,
    SkipButton,
    TagLineHeader,
    SubTitles,
    SwiggyLogo,
    TagLine,
    NoteVector,
    HeadingText,
} from "./SwiggyPlanBullsEyeTGHalfCard.styles";
import styles from "./SwiggyPlanBullsEyeTGHalfCard.scss";

import cx from "classnames";

import { AnalyticsContext } from "@swiggy-private/checkout-core";
import { getStrickedText } from "@swiggy-private/super-ui";

const SwiggyPlanBullsEyeTGHalfCard: React.FC<BullsEyeTGHalfCardProps> = ({
    bottomSheetData,
    addSuperFromTGBottomsheet,
    closeBottomSheet,
    analytics,
    isOneLite,
    context,
}) => {
    const { title, attribute_values, logo_ctx, subtitles, note } = bottomSheetData || ({} as M2CCardComms);
    const [addCta, skipCta] = bottomSheetData ? [...bottomSheetData.ctas] : ([{}, {}] as TGCTAs[]);
    const { impressionEvent } = useContext(AnalyticsContext);

    const backgroundImage = useMemo(() => getCloudinaryUrl(isOneLite ? HALF_RAY_BACKGROUND : FULL_RAY_BACKGROUND), [
        isOneLite,
    ]);

    useEffectOnce(() => {
        analytics?.impressionBullsEyeTgHalfcardMount(impressionEvent, context);
        analytics?.impressionBullsEyeTgAddCta(impressionEvent, addCta?.text, context);
        analytics?.impressionBullsEyeTgSkipCta(impressionEvent, skipCta?.text, context);
    });

    const formattedTitle = useMemo(() => {
        if (
            !isEmpty(attribute_values?.plan_info_text) &&
            typeof title === "string" &&
            title.includes(PLACEHOLDERS.PLAN_INFO_TEXT)
        ) {
            return title.replace(
                PLACEHOLDERS.PLAN_INFO_TEXT,
                `<span class=${styles.titleColor}>${attribute_values?.plan_info_text}</span>`,
            );
        }
        return "";
    }, [attribute_values, title]);

    const formattedSubtitles = useCallback(
        (subTitle: string) => {
            return getStrickedText(subTitle, attribute_values);
        },
        [attribute_values],
    );

    return (
        <BottomSheet
            onClose={closeBottomSheet}
            hideOnOverlayClick
            containerDataTestId={BULLS_EYE_TG.HALF_CARD}
            containerClass={styles.bottomSheet}
            closeButtonClassName={cx(styles.closeButton, { [styles.closeButtonLite]: isOneLite })}
        >
            {(): React.ReactElement => (
                <BullsEyeHalfCard backgroundImage={backgroundImage || ""} isOneLite={isOneLite}>
                    {!isEmpty(logo_ctx?.logo) && (
                        <SwiggyLogo isOneLite={isOneLite}>
                            {isOneLite ? (
                                <Image
                                    className={styles.oneLiteLogo}
                                    imageUrl={getCloudinaryUrl(logo_ctx?.logo || "")}
                                    alt="Swiggy one"
                                    lazy={false}
                                />
                            ) : (
                                <Image
                                    width={179}
                                    height={125}
                                    imageUrl={getCloudinaryUrl(logo_ctx?.logo || "")}
                                    alt="Swiggy Logo"
                                    lazy={false}
                                />
                            )}
                        </SwiggyLogo>
                    )}

                    {isOneLite && (
                        <DEAsset>
                            <Image
                                className={styles.deliveryExecutive}
                                imageUrl={getCloudinaryUrl(DELIVERY_EXECUTIVE)}
                                width={126}
                                height={113}
                            />
                        </DEAsset>
                    )}

                    <HalfCardContainer isOneLite={isOneLite}>
                        <HalfCardDetails>
                            <HeadingText
                                variant="V2.heading1.neutral.black"
                                color="text_Highest_Emphasis"
                                dangerouslySetInnerHTML={{ __html: formattedTitle }}
                            />

                            {!isEmpty(note) && (
                                // Somehow the snapshot test is failing for this element hence added data-testid direclty
                                <TagLineHeader data-testid={"SWIGGY_PLAN_BULLS_EYE_TG_TAGLINE"}>
                                    <NoteVector isRight={false}></NoteVector>
                                    <TagLine>
                                        <Text variant="V2.overline.neutral.bold" color="primary">
                                            {note}
                                        </Text>
                                    </TagLine>
                                    <NoteVector isRight={true}></NoteVector>
                                </TagLineHeader>
                            )}

                            {!isEmpty(subtitles) && (
                                <SubTitles>
                                    {typeof subtitles !== "string" &&
                                        subtitles?.map((subTitle: string) => (
                                            <Text
                                                key={subTitle}
                                                variant="V2.body1.neutral.regular"
                                                color="text_High_Emphasis"
                                                dangerouslySetInnerHTML={{ __html: formattedSubtitles(subTitle) }}
                                            />
                                        ))}
                                </SubTitles>
                            )}

                            {!isEmpty(addCta?.text) && (
                                <Button
                                    variant="primary"
                                    buttonSize="large"
                                    onClick={() => addSuperFromTGBottomsheet(addCta?.text)}
                                    data-testid={BULLS_EYE_TG.HALF_CARD_SUCCESS_CTA}
                                >
                                    <Text variant="V2.subheading1.neutral.bold" color="text_inverse_Highest_Emphasis">
                                        {addCta?.text}
                                    </Text>
                                </Button>
                            )}

                            {!isEmpty(skipCta?.text) && (
                                <SkipButton
                                    onClick={() => closeBottomSheet(skipCta?.text)}
                                    data-testid={BULLS_EYE_TG.HALF_CARD_SKIP_CTA}
                                >
                                    <Text variant="V2.subheading1.neutral.bold" color="primary">
                                        {skipCta?.text}
                                    </Text>
                                </SkipButton>
                            )}
                        </HalfCardDetails>
                    </HalfCardContainer>
                </BullsEyeHalfCard>
            )}
        </BottomSheet>
    );
};

export default React.memo(SwiggyPlanBullsEyeTGHalfCard);
